import { Injectable } from '@angular/core';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { APP_CONFIG } from '../../environment';
import { HttpProvider } from '../http/http';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { RouteProvider } from '../route/route';
import { LoadingProvider } from '../loading/loading';
import { LoginProvider } from '../myria/login-provider';
import { COUNTRY_SETTINGS, KYC_ATTEMPT_STATUS, KYC_METHODS } from '../myria/app-variables';
import { initiateVerificationResponse } from '../../model/api/ekyc-singpass/ekyc-singpass.entity.interface';
import { SafariViewController, SafariViewControllerOptions } from '@ionic-native/safari-view-controller';
import { ColorProvider } from '../../providers/color/color';
import { Platform } from 'ionic-angular';
import { DeeplinkProvider } from '../../providers/marketing/deeplink';
import { AddressSource, AnalyticsEventType, SegmentLocationNames } from '../../model/types/analytics.const';
import { SegmentProvider } from '../../providers/analytics/segment';
import { EKYC_PAGEFROM, EKYC_SINGPASS_RESPONSE, SINGPASS_SETTING } from '../../model/models/ekyc-singpass/ekyc-singpass.model';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { CustomerProvider } from '../../providers/customer/customer';
import { BranchProvider } from '../../providers/analytics/branch';
import { getSourceAnalyticsData } from '../../providers/analytics/deeplink-source';
import { OnfidoProvider } from '../../providers/myria/onfido-provider';
import { OrderProvider } from '../../providers/order/order';

declare var window: any;
@Injectable()
export class EkycSingPassProvider {

  constructor(private httpProvider: HttpProvider, private routeProvider: RouteProvider, private iab: InAppBrowser, private platform: Platform,
    private loadingProvider: LoadingProvider, private loginProvider: LoginProvider, private tabsView: SafariViewController, 
    private deeplinkProvider: DeeplinkProvider, private analytics: SegmentProvider, private utilsProvider: UtilsProvider, private onfidoProvider: OnfidoProvider,
    private customerProvider: CustomerProvider, private branchProvider: BranchProvider, private orderProvider: OrderProvider
  ) { }
  
  getSingpassAuthorizationUrl(pageLoadFrom: string): Promise<initiateVerificationResponse> {
    try {
      const endPoint: string = pageLoadFrom ? `initiateverification?workflowstep=${pageLoadFrom}` : 'initiateverification';
      return this.httpProvider.get(`${APP_CONFIG.API_URL}api/kyc/singpass/`+ endPoint).catch((err) => {
        this.loadingProvider.hide();
      })
    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.Singpass, "Singpass get authorization Url failed");
      return null;
    }
  }

  getSingpassUserInfo(reqBody): Promise<any> {
    try {
      return this.httpProvider.post(`${APP_CONFIG.API_URL}api/kyc/singpass/getUserInfo`, reqBody);
    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.Singpass, "Singpass get authorization Url failed");
      return null;
    }
  }

  singpassUpdateEkycStatus(reqBody): Promise<any> {
    try {
      return this.httpProvider.post(`${APP_CONFIG.API_URL}api/kyc/singpass/endverification`, reqBody);
    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.Singpass, "Singpass get authorization Url failed");
      return null;
    }
  }

  singpassRegisterValidationUserInfo(reqState): Promise<any> {
    try{
      return this.httpProvider.post(`${APP_CONFIG.API_URL}api/kyc/singpass/validateUserInfo`, reqState)
    } catch(ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.Singpass, "Failed to Validate singpass user info");
      return null;
    }
  }

  async ekycSingpassAuthorizationUrl(pageLoadFrom, isSendAgain) {
    this.loadingProvider.show();
    try {
      const singpass = await this.getSingpassAuthorizationUrl(pageLoadFrom);
      if (singpass && singpass.Result && singpass.Value && singpass.Result.RetVal === 0 && singpass.Value.Url) {
        this.loadingProvider.hide();
        DeeplinkProvider.canRedirectToSingpass = true;
        this.analyticEventIdentityVerificationStarted(pageLoadFrom);
        this.deeplinkProvider.setDeeplinkPendingOrder(true); // set deeplink pending order
        const options: SafariViewControllerOptions = {
          url: singpass.Value.Url,
          tintColor: ColorProvider.getColorValue('--app-primary'), // default is ios blue
          barColor: ColorProvider.getColorValue('--gray-100'), // on iOS 10+ you can change the background color as well
          controlTintColor: ColorProvider.getColorValue('--app-primary'), // on iOS
          showDefaultShareMenuItem: false
        };
        if (this.platform.is('ios')) {
          window.plugins.ASWebAuthSession.start("riamoneytransfer", options.url,
            (msg) => {
              LoggerProvider.info(this, "ASWebAuthSession success : + " + msg);
              this.deeplinkProvider.processToSingpassDeeplink(null, msg)
            }, (err) => {
              if (DeeplinkProvider.canRedirectToSingpass) {
                LoggerProvider.error(this, LoggerEvent.Signicat, 'ASWebAuthSession error', err);
                this.analyticEventIdentityVerificationStarted(pageLoadFrom, 'Web view issue');
                this.routeProvider.openModal(this.routeProvider.pages.SingpassGenericErrorModal)
              } else
                LoggerProvider.info(this, 'ASWebAuthSession closed', err);
            });
        } else {
          await this.tabsView.isAvailable();

          this.tabsView.show(options).subscribe(
            async (res) => {
                if (res.event === 'closed') {
                LoggerProvider.info(this, 'Singpass tabs view closed');
                try {
                  LoggerProvider.info(this, 'Singpass verification res', res);
                } catch (ex) {
                  this.loadingProvider.hide();
                  this.analyticEventIdentityVerificationStarted(pageLoadFrom, 'Web view issue');
                  this.routeProvider.openModal(this.routeProvider.pages.SingpassGenericErrorModal)
                }
              }
            },
            (err) => {
              this.analyticEventIdentityVerificationStarted(pageLoadFrom, 'Web view issue');
              this.routeProvider.openModal(this.routeProvider.pages.SingpassGenericErrorModal)
            });
        }

      } else {
        this.loadingProvider.hide();
        this.analyticEventIdentityVerificationStarted(pageLoadFrom, 'Web view issue');
        this.routeProvider.openModal(this.routeProvider.pages.SingpassGenericErrorModal)
      }
    } catch (ex) {
      this.loadingProvider.hide();
      this.analyticEventIdentityVerificationStarted(pageLoadFrom, 'Web view issue');
      LoggerProvider.exception(this, ex, LoggerEvent.Singpass, "Singpass Web view issue");
      this.routeProvider.openModal(this.routeProvider.pages.SingpassGenericErrorModal)
    }
  }

  async isUsedEkycSinpass(isCheckVerified: boolean = false):Promise<boolean>{
    const user = await this.loginProvider.getUserAsync();
    if(isCheckVerified)
      return (user && user.IdentityVerification && user.IdentityVerification.Status === KYC_ATTEMPT_STATUS.VERIFIED); // singpass Processor
    else{
      const isUpdateWithSingpass = await this.loginProvider.getCustomerSettingAsync(SINGPASS_SETTING.SETTING_VAL);
      let isSingpasProcessCheck = (user && user.IdentityVerification && user.IdentityVerification.Processor === SINGPASS_SETTING.PROCESSOR) ? true : false
      let isSingpass = (isSingpasProcessCheck || isUpdateWithSingpass == 'true' || isUpdateWithSingpass == '1') ? true : false;
      return isSingpass
    } 
  }

  async isUserLastAddressObtainedFromSingpass(forceRefresh: boolean = false): Promise<boolean> {
    if(forceRefresh) await this.loginProvider.getUserAsync(true);
    const settingValue = await this.loginProvider.getCustomerSettingAsync(SINGPASS_SETTING.ACCOUNT_ADDRESS_UPDATED_WITH_SINGPASS);
    return (settingValue == 'true' || settingValue == '1');
  }

  async isShowSingpassConfirmAddress(): Promise<boolean> {
    let returnValue: boolean = false;
    const user = await this.loginProvider.getUserAsync();
    const isAddressUpdatedWithSingpass = await this.isUserLastAddressObtainedFromSingpass(); // Check if address is updated with Singpass
    const lastKycDateStr = await this.loginProvider.getCustomerSettingAsync(SINGPASS_SETTING.LAST_UPDATED_SINGPASS_KYC_DATE);
    const lastAddressDateStr = await this.loginProvider.getCustomerSettingAsync(SINGPASS_SETTING.LAST_UPDATED_ADDRESS_DATE);
    const enableEkycSingpass = await this.utilsProvider.isSingpassEnable(); // Check if eKYC Singpass is enabled
    const userHasAddress = user && user.Customers[0] && user.Customers[0].Address !== '' ? true : false;
    if(enableEkycSingpass && lastKycDateStr && lastAddressDateStr && !isAddressUpdatedWithSingpass && userHasAddress){
      const lastKycDate = new Date(lastKycDateStr);
      const lastAddressDate = new Date(lastAddressDateStr);
      if (lastKycDate > lastAddressDate) {
        return returnValue = true;
      }
    }
    return returnValue;
  }

  analyticEventIdentityVerificationRequired(pageLoadFrom){
    const objOrder = this.orderProvider.getCurrentOrder();
    
    this.analytics.trackEvent({ 
      eventName: AnalyticsEventType.IdentityVerificationRequired, 
      analyticsData: {
        location: pageLoadFrom,
        country: APP_CONFIG.region.defaultCountryFrom,
        destinationCountry: objOrder && objOrder.countryTo,
        verificationMethod: SegmentLocationNames.Singpass,
        amount: objOrder && SegmentProvider.formatNumber(objOrder.amountFrom),
        currency: objOrder && objOrder.currencyFrom,
        amountUSD: objOrder && SegmentProvider.formatNumber(objOrder.sendAmountUSD),
        ...(objOrder && objOrder.kycRequirementRule && objOrder.kycRequirementRule.length && { eKYCRule: objOrder.kycRequirementRule }),
        ...(objOrder && objOrder.kycRequirementReasons && objOrder.kycRequirementReasons.length && { eKYCReason: objOrder.kycRequirementReasons })
      }
    });
  }

  analyticEventIdentityVerificationStarted(pageLoadFrom, verificationError?){
    const objOrder = this.orderProvider.getCurrentOrder();
    this.analytics.trackEvent({ eventName: AnalyticsEventType.IdentityVerificationStarted,
      analyticsData: {
        location: pageLoadFrom,
        idType: KYC_METHODS.SINGPASS,
        country: APP_CONFIG.region.defaultCountryFrom,
        destinationCountry: objOrder && objOrder.countryTo,
        verificationMethod: KYC_METHODS.SINGPASS,
        amount: objOrder && SegmentProvider.formatNumber(objOrder.amountFrom),
        currency: objOrder && objOrder.currencyFrom,
        amountUSD: objOrder && SegmentProvider.formatNumber(objOrder.sendAmountUSD),
        verificationError: verificationError || ''
      }
    });
  }

  alyticEventIdentityVerificationCompleted(pageLoadFrom, verificationError?){
    const objOrder = this.orderProvider.getCurrentOrder();
    this.analytics.trackEvent({ eventName: AnalyticsEventType.IdentityVerificationCompleted,
      analyticsData: {
        location: pageLoadFrom,
        idType: KYC_METHODS.SINGPASS,
        country: APP_CONFIG.region.defaultCountryFrom,
        destinationCountry: objOrder && objOrder.countryTo,
        verificationMethod: KYC_METHODS.SINGPASS,
        amount: objOrder && SegmentProvider.formatNumber(objOrder.amountFrom),
        currency: objOrder && objOrder.currencyFrom,
        amountUSD: objOrder && SegmentProvider.formatNumber(objOrder.sendAmountUSD),
        verificationError: verificationError || ''
      }
    });
  }

  async userProfileSingpassUpdate(isAddressPage: boolean = false, personal = null){
    let isBlock = await this.isUserBlock();
    if(isBlock && isBlock.isUserBlock){
      this.routeProvider.push(this.routeProvider.pages.SingpassVerificationFailedPage, {
        isWaiting: false,
        errCode: isBlock.errCode,
        pageLoadFrom: EKYC_PAGEFROM.SETTINGS,
      })

    } else {
      const isUserVerified = await this.isUsedEkycSinpass(true)
      const enableEkycSingpass = await this.utilsProvider.isSingpassEnable();
      const isUpdateWithSingpass = await this.loginProvider.getCustomerSettingAsync(SINGPASS_SETTING.SETTING_VAL);
      const isUsedEkycSinpass = await this.isUsedEkycSinpass();
      const isCustomerIDExpired = await this.loginProvider.isCustomerIDInvalid();
      const user = await this.loginProvider.getUserAsync();
      const attemptsLeft = user && user.IdentityVerification && user.IdentityVerification.AttemptsLeft || 0;
      if(enableEkycSingpass){
        if(isAddressPage){
          const isAddressUpdatedWithSingpass = await this.isUserLastAddressObtainedFromSingpass(); // if address is updated with singpass only than we can proceed to singpass login page
          if (isAddressUpdatedWithSingpass && (isUserVerified && isUsedEkycSinpass) || (isUsedEkycSinpass && isCustomerIDExpired)) {
            this.redirectToSingpassOrUpdateId(attemptsLeft);
          } else{
            this.routeProvider.push(this.routeProvider.pages.SettingsPersonalPage, { 'profileAddressUp': true, 'personal': personal });
          }
        } else{
          if (isUsedEkycSinpass && isCustomerIDExpired) {
            this.redirectToSingpassOrUpdateId(attemptsLeft);
          } else if((!isUserVerified || isUpdateWithSingpass === "false" || isCustomerIDExpired) && attemptsLeft > 0){
            this.routeProvider.openModal(this.routeProvider.pages.SingpassProfileVerifyMethodSelectPage, { pageLoadFrom: EKYC_PAGEFROM.SETTINGS });
          } else{ 
            this.routeProvider.openModal(this.routeProvider.pages.UpdateIdOnStorePage)
          }
        }
      } else {
        if(isAddressPage) this.routeProvider.push(this.routeProvider.pages.SettingsPersonalPage, { 'profileAddressUp': true, 'personal': personal });
        else this.routeProvider.openModal(this.routeProvider.pages.UpdateIdOnStorePage)
      }
    }
  }

  private redirectToSingpassOrUpdateId(attemptsLeft: number){
    if (attemptsLeft > 0) {
      this.analyticEventIdentityVerificationRequired(SegmentLocationNames.Settings);
      this.ekycSingpassAuthorizationUrl(EKYC_PAGEFROM.SETTINGS, false);
    } else {
      this.routeProvider.openModal(this.routeProvider.pages.UpdateIdOnStorePage);
    }
  }

  analyticEventRegistrationFailed(pageLoadFrom, error){
    this.analytics.trackEvent({eventName: AnalyticsEventType.RegistrationFailed, analyticsData:{
      location: pageLoadFrom,
      reason: error,
      registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
      country: APP_CONFIG.region.defaultCountryFrom,
      userLocale: this.customerProvider.getCurrentLanguage(),
      email: this.httpProvider.getAuthEmail(),
    }});
  }

  analyticEventRegistration(customer, user, location){
    let phone , city, state , zipCode, country;

    if(user && user.LastVerifiedPhone){
      phone = user.LastVerifiedPhone.CountryCode;
    }

    if(customer.address){
      let address = customer.address;
      city = address.city ? address.city : '';
      state = address.stateProvince ? address.stateProvince : '';
      zipCode = address.postCode ? address.postCode : '';
      country = APP_CONFIG.region.defaultCountryFrom
    }

    let phoneCountry = "";
    if(user.LastVerifiedPhone && user.LastVerifiedPhone.CountryCode){
      phoneCountry = user.LastVerifiedPhone.CountryCode;
    } else if(this.analytics && this.analytics.lastIdentifyData && this.analytics.lastIdentifyData['phoneCountry']) {
      phoneCountry = this.analytics.lastIdentifyData['phoneCountry'];
    }

    this.analytics.trackEvent({eventName: AnalyticsEventType.ProfileCreated, analyticsData: {
      location: location,
      email: this.httpProvider.getAuthEmail(),
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      phoneCountry: phone,
      registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
    }});

    this.analytics.trackEvent({eventName: AnalyticsEventType.AddressCreated, analyticsData: {
      location: location,
      city: city,
      state: state,
      zipCode: zipCode,
      country: country,
      registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
      addressSource: AddressSource.Singpass,
    }});

    this.analytics.trackEvent({eventName: AnalyticsEventType.RegistrationCompleted, analyticsData:{
      location: location,
      registrationFlowType: this.utilsProvider.getRegistrationFlowType(),
      city: city,
      state: state,
      zipCode: zipCode,
      phoneCountry: phoneCountry,
      country: APP_CONFIG.region.defaultCountryFrom,
      userLocale: this.customerProvider.getCurrentLanguage(),
      email: this.httpProvider.getAuthEmail(),
      ...getSourceAnalyticsData()
    }});

    this.branchProvider.trackRegistration(customer);
    this.analytics.identifyCustomer(customer);
  }  

  async isUserBlock(){
    const isBlock = await this.loginProvider.getUserSetting(SINGPASS_SETTING.ACCOUNT_BLOCK);
    let errCode;
    if(isBlock && isBlock == '1'){
      errCode = EKYC_SINGPASS_RESPONSE.ALIAS_NAME_PRESENT;
      const userBlockUntil = await this.loginProvider.getUserSetting(SINGPASS_SETTING.ACCOUNT_BLOCK_UNTIL);
      const today = new Date();
      if(userBlockUntil && new Date(userBlockUntil.toString()).getTime() < new Date(today).getTime()){
        return { errCode : errCode, isUserBlock: false }
      } else {
        errCode = EKYC_SINGPASS_RESPONSE.NOT_MEET_AGE_REQ;
      }
    }
    return { errCode : errCode, isUserBlock: errCode && this.loginProvider.isLiteUser() ? true : false}
  }
}



// WEBPACK FOOTER //
// ./src/providers/ekyc-singpass/ekyc-singpass.ts